import { useEffect, useReducer } from 'react';
import { queuedFetch } from '../lib/fetchQueue';

/**
 * Yields every YYYY-MM from `start` (inclusive) to `end` (exclusive).
 * Both args are strings 'YYYY-MM'.
 */
export function monthsBetween(start, end) {
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  const out = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m < em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m === 13) { m = 1; y += 1; }
  }
  return out;
}

function reducer(state, action) {
  switch (action.type) {
    case 'start':  return { ...state, loading: { ...state.loading, [action.month]: true } };
    case 'ok':     return {
      byMonth: { ...state.byMonth, [action.month]: action.summary },
      loading: { ...state.loading, [action.month]: false },
      error: { ...state.error, [action.month]: null },
    };
    case 'fail':   return {
      ...state,
      loading: { ...state.loading, [action.month]: false },
      error: { ...state.error, [action.month]: action.error },
    };
    default: return state;
  }
}

const initial = { byMonth: {}, loading: {}, error: {} };

/**
 * Fetches /api/cash-close/summary for every month in [startYearMonth, currentYearMonth)
 * via the FetchQueue (serialized) so Render free tier does not 503.
 *
 * Returns: { byMonth: { 'YYYY-MM': summary }, loading: {...}, error: {...} }
 *
 * Re-runs when companyId or the bounds change.
 */
export function useMultiYearActuals({ companyId, startYearMonth, currentYearMonth, backendUrl }) {
  const [state, dispatch] = useReducer(reducer, initial);

  useEffect(() => {
    if (!companyId || !startYearMonth || !currentYearMonth) return;
    let cancelled = false;
    const months = monthsBetween(startYearMonth, currentYearMonth);
    months.forEach((month) => {
      dispatch({ type: 'start', month });
      queuedFetch(
        `${backendUrl}/api/cash-close/summary?company_id=${encodeURIComponent(companyId)}&month=${month}&persist=false`
      )
        .then(async (r) => {
          if (cancelled) return;
          if (!r.ok) { dispatch({ type: 'fail', month, error: `HTTP ${r.status}` }); return; }
          const summary = await r.json();
          dispatch({ type: 'ok', month, summary });
        })
        .catch((e) => {
          if (cancelled) return;
          dispatch({ type: 'fail', month, error: String(e) });
        });
    });
    return () => { cancelled = true; };
  }, [companyId, startYearMonth, currentYearMonth, backendUrl]);

  return state;
}
